/* eslint-disable react-hooks/exhaustive-deps */
import { codeOk, codeCreate } from "../../lib/index";
import { BsCheckCircle } from "react-icons/bs";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FaTimes } from "react-icons/fa";
import { useEffect, useRef } from "react";

/**
 * Message
 * - Visual-only toast card with white surface, soft shadow, and colored left accent.
 * - Keeps click-to-dismiss via props.onClick.
 * - Uses aria-live for screen readers.
 */
export const Message = ({ props }) => {
  const { error, success, status, message, onClick } = props || {};

  const isSuccess = status === codeOk || status === codeCreate;
  const shouldShow = error === true || success === true;
  if (!shouldShow) return null;

  return (
    <div
      role="alert"
      aria-live={isSuccess ? "polite" : "assertive"}
      onClick={onClick}
      className={[
        "absolute z-[999999] top-[70px] right-4 xl:right-8",
        "w-[calc(100%-2rem)] xl:w-[480px]",
        "rounded-2xl border bg-white shadow-2xl cursor-pointer",
        "transition hover:shadow-[0_12px_28px_-10px_rgba(0,0,0,0.25)]",
        isSuccess ? "border-emerald-200" : "border-red-200",
      ].join(" ")}
    >
      <div className="relative flex w-full items-stretch overflow-hidden rounded-2xl">
        {/* Accent bar */}
        <div
          className={[
            "w-1",
            isSuccess ? "bg-emerald-500" : "bg-red-500",
          ].join(" ")}
        />

        {/* Content */}
        <div className="flex w-full items-center justify-between gap-3 p-3 sm:p-4">
          <div className="flex items-start gap-3">
            {isSuccess ? (
              <BsCheckCircle className="h-6 w-6 shrink-0 text-emerald-600" />
            ) : (
              <AiOutlineCloseCircle className="h-6 w-6 shrink-0 text-red-600" />
            )}

            <div className="min-w-0">
              <p
                className={[
                  "text-[11px] font-semibold uppercase tracking-wide",
                  isSuccess ? "text-emerald-800" : "text-red-800",
                ].join(" ")}
              >
                {isSuccess ? "Success" : "Error"}
              </p>
              <p className="mt-0.5 text-sm leading-5 text-gray-800 break-words">
                {message}
              </p>
            </div>
          </div>

          {/* Close affordance (kept for parity with original) */}
          <div className="ml-2 flex h-10 items-center">
            <div
              className={[
                "inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-xs font-semibold",
                "text-gray-700 hover:bg-gray-50",
                isSuccess
                  ? "border-emerald-200"
                  : "border-red-200",
              ].join(" ")}
            >
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * MyMessage
 * - Auto-dismiss after 4s (fixes timer cleanup).
 * - White card, colored accent, a11y improvements.
 * - Maintains onClick to dismiss immediately.
 */
export const MyMessage = ({ error, success, onClick, message, onExpired }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    // Start / reset timer when a message is shown
    if (error || success) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onExpired && onExpired();
      }, 4000);
    }
    // Cleanup
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [error, success, onExpired]);

  if (!error && !success) return null;

  const isSuccess = !!success;

  return (
    <div
      role="alert"
      aria-live={isSuccess ? "polite" : "assertive"}
      onClick={onClick}
      className={[
        "absolute z-[99999] top-36 right-6",
        "w-[calc(100%-3rem)] sm:w-[420px]",
        "rounded-2xl border bg-white shadow-2xl cursor-pointer",
        "transition hover:shadow-[0_12px_28px_-10px_rgba(0,0,0,0.25)]",
        isSuccess ? "border-emerald-200" : "border-red-200",
      ].join(" ")}
    >
      <div className="relative flex items-stretch overflow-hidden rounded-2xl">
        {/* Accent bar */}
        <div className={["w-1", isSuccess ? "bg-emerald-500" : "bg-red-500"].join(" ")} />

        {/* Content */}
        <div className="flex w-full items-center justify-between gap-3 p-3 sm:p-4">
          <div className="flex items-center gap-3">
            {isSuccess ? (
              <BsCheckCircle className="h-5 w-5 text-emerald-600" />
            ) : (
              <AiOutlineCloseCircle className="h-5 w-5 text-red-600" />
            )}
            <p className="text-sm font-medium text-gray-800">
              {isSuccess ? "Success" : `Error: ${message}`}
            </p>
          </div>

          <div
            className={[
              "flex items-center rounded-lg border px-3 py-1.5",
              isSuccess ? "border-emerald-200" : "border-red-200",
            ].join(" ")}
          >
            <FaTimes className="h-4 w-4 text-gray-600 hover:text-gray-800" />
          </div>
        </div>
      </div>
    </div>
  );
};
